import type { IconName } from "@nexus/ui";
import type { ChangelogSettingKey } from "@/features/changelog/lib/changelog-settings";

// ─── Release settings groups ────────────────────────────────────────
// Twenty-three settings across seven cards is more than anyone reads in one
// scroll. Each group is one rail entry and one panel; the record behind them
// and its save footer stay shared.

export const CHANGELOG_SETTINGS_GROUPS = [
  "automation",
  "releases",
  "roundups",
  "notes",
  "ops",
] as const;

export type ChangelogSettingsGroup = (typeof CHANGELOG_SETTINGS_GROUPS)[number];

/**
 * Where the section opens: the preset question. It is the only thing most
 * admins came to change, and it is what sets the switches in the groups under
 * it — so it stays the front door rather than becoming one entry among equals.
 */
export const DEFAULT_CHANGELOG_SETTINGS_GROUP: ChangelogSettingsGroup = "automation";

interface ChangelogGroupMeta {
  icon: IconName;
  labelKey: string;
  labelFallback: string;
  /**
   * The settings this group owns, for the unsaved marker. The automation group
   * owns none of its own: a preset writes the approval switches that live in
   * the releases and roundups groups, and marking those is what tells an admin
   * what the preset just changed.
   */
  keys: readonly ChangelogSettingKey[];
}

export const CHANGELOG_SETTINGS_GROUP_META: Record<ChangelogSettingsGroup, ChangelogGroupMeta> = {
  automation: {
    icon: "sliders-horizontal",
    labelKey: "admin.changelog.settings.groups.automation",
    labelFallback: "Automation",
    keys: [],
  },
  releases: {
    icon: "shield",
    labelKey: "admin.changelog.settings.groups.releases",
    labelFallback: "Release posts",
    keys: [
      "requireDeployApproval",
      "autoPublishDelayMinutes",
      "minItemsToAutoPublish",
      "quietHoursEnabled",
      "quietHoursStartMinute",
      "quietHoursEndMinute",
      "timezone",
    ],
  },
  roundups: {
    icon: "mail",
    labelKey: "admin.changelog.settings.groups.roundups",
    labelFallback: "Roundups",
    keys: [
      "requireRoundupApproval",
      "roundupAutoSendDelayMinutes",
      "roundupNotifyEmail",
      "roundupNotifySlack",
      "autoComposeRoundups",
      "minDeploysToAutoCompose",
      "roundupCadence",
      // The sprint definition sits on this panel: it is what the SPRINT cadence
      // above means, and it is dead weight anywhere else.
      "sprintLengthDays",
      "sprintAnchorDate",
      "sprintAnchorNumber",
    ],
  },
  notes: {
    icon: "sparkles",
    labelKey: "admin.changelog.settings.groups.notes",
    labelFallback: "Notes & announcements",
    keys: ["notesAudience", "includeJiraLinks", "maxItemsPerSection", "userFacingAnnouncements"],
  },
  ops: {
    icon: "refresh-cw",
    labelKey: "admin.changelog.settings.groups.ops",
    labelFallback: "Syncing & alerts",
    keys: ["autoSyncOnOpen", "notifyOnFailure"],
  },
};

/** Which groups hold an unsaved change, so the rail can mark them. */
export function changelogGroupsWithChanges(
  changedKeys: readonly string[],
): Set<ChangelogSettingsGroup> {
  const changed = new Set(changedKeys);

  return new Set(
    CHANGELOG_SETTINGS_GROUPS.filter((group) =>
      CHANGELOG_SETTINGS_GROUP_META[group].keys.some((key) => changed.has(key)),
    ),
  );
}
